import { useEffect, useRef, useState } from "react";
import classes from "classnames";
import styles from "./index.module.scss";
import { IconArrowLeft, IconClose1, Loading } from "@quark/design";
import usePage from "../../hooks/use-page";
import { useNavigate, useParams } from "react-router-dom";
import Knowledges from "../../component/knowledges";
import QuestionRenderer from "../../component/question-renderer";
import {
  Knowledge,
  Question,
  KnowledgeType,
  PageComponent,
  QuizRefContent,
  ChallengeRefContent,
  MarkdownContent,
} from "../../types";
import { submitSlideProgress } from "../../api";
import ProgressWithStepper from "../../component/progress-with-stepper";
import { ChatGptDrawerProvider } from "../../component/chat-gpt-drawer";
import SnapshotWrapper from "../../component/snapshot-wrapper";
import useDocumentTitle from "../../hooks/use-document-title";
import Challenge from "../../component/challenge";

const Index = () => {
  const navigate = useNavigate();
  const [currStep, setCurrStep] = useState(1);
  const [lastStep, setLastStep] = useState(1);
  const progressInitialized = useRef(false);
  const { courseId, pageId } = useParams<{
    courseId: string;
    pageId: string;
  }>();

  const { page, slides, loading, handleSubmitAnswer } = usePage(
    courseId!,
    pageId!
  );

  useDocumentTitle(page?.title + ":" + currStep + "/" + slides?.length);

  useEffect(() => {
    if (!slides.length) {
      return;
    }
    if (progressInitialized.current) {
      return;
    }
    progressInitialized.current = true;

    let lastLearnIndex = 0;
    slides.forEach((slide, index) => {
      if (slide.type === "quiz-question") {
        const data = slide.data as Question;
        if (data.userAnswer?.length) {
          lastLearnIndex = index;
        }
      }
      if (slide.type === "edu-challenge") {
        const data = slide.data as Record<string, any>;
        if (data.user_data) {
          lastLearnIndex = index;
        }
      }
    });

    if (lastLearnIndex + 1 === slides.length) {
      setCurrStep(1);
    } else {
      setCurrStep(lastLearnIndex + 1);
    }

    setLastStep(lastLearnIndex + 1);
  }, [slides]);

  useEffect(() => {
    if (page?.status === "lock") {
      navigate("../../catalog", {
        relative: "path",
      });
    }
  }, [page, navigate]);

  const handleNext = () => {
    const curSlide = slides[currStep - 1];
    if (curSlide.type === "quiz-question") {
      const component = page?.components?.find?.((c: PageComponent<any>) => {
        return (c.content as QuizRefContent)?.questionId === curSlide.id;
      });
      if (component) {
        submitSlideProgress(courseId!, pageId!, component.content.componentId);
      }
    }

    if (curSlide.type === "edu-challenge") {
      const component = page?.components?.find?.((c: PageComponent<any>) => {
        return (c.content as ChallengeRefContent)?.challengeId === curSlide.id;
      });
      if (component) {
        submitSlideProgress(courseId!, pageId!, component.content.componentId);
      }
    }

    if (curSlide.type === "edu-knowledge") {
      const component = page?.components?.find?.((c: PageComponent<any>) => {
        return (c.content as MarkdownContent)?.knowledgeId === curSlide.id;
      });
      if (component) {
        submitSlideProgress(courseId!, pageId!, component.content.componentId);
      }
    }

    if (currStep === slides.length) {
      navigate("completed");
    }

    if (currStep < slides.length) {
      setCurrStep(currStep + 1);
      if (lastStep < currStep + 1) {
        setLastStep(currStep + 1);
      }
    }
  };

  const handlePrev = () => {
    if (currStep > 1) {
      setCurrStep(currStep - 1);
    }
  };

  const renderSlide = () => {
    if (!slides) {
      return null;
    }
    const slide = slides[currStep - 1];

    if (slide.type === "edu-challenge") {
      return (
        <SnapshotWrapper
          courseInfo={{
            course_no: courseId!,
            page_id: Number(pageId),
            step: currStep,
          }}
        >
          <Challenge
            step={currStep}
            data={slide.data}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        </SnapshotWrapper>
      );
    }

    if (slide.type === "quiz-knowledge" || slide.type === "edu-knowledge") {
      return (
        <SnapshotWrapper
          courseInfo={{
            course_no: courseId!,
            page_id: Number(pageId),
            step: currStep,
          }}
        >
          <Knowledges
            step={currStep}
            title={slide.title}
            knowledges={slide.data as Knowledge<KnowledgeType>[]}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        </SnapshotWrapper>
      );
    }
    if (slide.type === "quiz-question") {
      return (
        <SnapshotWrapper
          courseInfo={{
            course_no: courseId!,
            page_id: Number(pageId),
            step: currStep,
          }}
        >
          <QuestionRenderer
            key={slide.id}
            id={slide.id}
            onPrev={handlePrev}
            onNext={handleNext}
            onSubmitAnswer={handleSubmitAnswer}
            title={slide.title}
            question={slide.data as Question}
            courseNo={courseId!}
          />
        </SnapshotWrapper>
      );
    }
    return null;
  };

  const handleLeftTitleClick = () => {
    navigate("../../catalog", {
      relative: "path",
    });
  };

  if (loading) {
    return <Loading fullscreen />;
  }

  return (
    <ChatGptDrawerProvider courseNo={courseId!}>
      <div className={styles.chapter}>
        <div className={styles.header}>
          <div className={classes(styles.navigate, styles.left)}>
            <div className={styles.trigger} onClick={handleLeftTitleClick}>
              <IconClose1 className={styles.icon} />
              <div className={styles.text}>{page?.title}</div>
            </div>
          </div>
          <div className={classes("container", styles.center)}>
            <ProgressWithStepper
              currStep={currStep}
              lastStep={lastStep}
              total={slides.length}
            />
          </div>
          <div className={classes(styles.navigate, styles.right)}>
            <div className={styles.trigger}>
              <IconArrowLeft className={styles.icon} />
              <div className={styles.text}>笔记功能</div>
            </div>
          </div>
        </div>
        {renderSlide()}
      </div>
    </ChatGptDrawerProvider>
  );
};

export default Index;
